import React, { useMemo, useState } from 'react';
import type { CanvasComponent, RestAction, Variable } from '../../types/project';
import { insertRequestInput, requestInputSources } from './requestInputUtils';
import type { RequestInputTarget } from './requestInputUtils';
import styles from './ActionInspector.module.css';

type Props = {
  action: RestAction;
  components: CanvasComponent[];
  variables: Record<string, Variable>;
  onChange: (action: RestAction) => void;
};

function encodeTarget(target: RequestInputTarget): string {
  return target.location === 'headers' || target.location === 'queryParameters'
    ? `${target.location}:${target.key}`
    : target.location;
}

function decodeTarget(value: string): RequestInputTarget | null {
  if (value === 'url' || value === 'bodyTemplate') return { location: value };
  const separator = value.indexOf(':');
  if (separator < 0) return null;
  const location = value.slice(0, separator);
  const key = value.slice(separator + 1);
  if ((location === 'headers' || location === 'queryParameters') && key) return { location, key };
  return null;
}

export default function RequestInputEditor({ action, components, variables, onChange }: Props): React.ReactElement {
  const sources = useMemo(() => requestInputSources(components, variables), [components, variables]);
  const targets = useMemo<RequestInputTarget[]>(() => [
    { location: 'url' },
    ...Object.keys(action.headers ?? {}).map((key) => ({ location: 'headers' as const, key })),
    ...Object.keys(action.queryParameters ?? {}).map((key) => ({ location: 'queryParameters' as const, key })),
    { location: 'bodyTemplate' },
  ], [action.headers, action.queryParameters]);
  const [sourceId, setSourceId] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const source = sources.find((candidate) => candidate.id === sourceId);
  const target = decodeTarget(targetValue);

  return (
    <div className={styles.requestInputEditor} data-testid="request-input-editor">
      <div className={styles.formLabel}>Request value reference</div>
      <div className={styles.formHint}>
        Insert an executed component or variable template. Header and query destinations replace that value; URL and body destinations append.
      </div>
      <div className={styles.requestInputRow}>
        <label className={styles.formField}>
          <span className={styles.formLabel}>Request destination</span>
          <select className={styles.formSelect} data-testid="request-input-target" value={targetValue} onChange={(event) => setTargetValue(event.target.value)}>
            <option value="">Select request destination</option>
            {targets.map((candidate) => {
              const value = encodeTarget(candidate);
              const label = candidate.location === 'url'
                ? 'Endpoint URL (append)'
                : candidate.location === 'bodyTemplate'
                  ? 'Body template (append)'
                  : `${candidate.location === 'headers' ? 'Header' : 'Query'}: ${candidate.key}`;
              return <option key={value} value={value}>{label}</option>;
            })}
          </select>
        </label>
        <label className={styles.formField}>
          <span className={styles.formLabel}>Value source</span>
          <select className={styles.formSelect} data-testid="request-input-source" value={sourceId} onChange={(event) => setSourceId(event.target.value)}>
            <option value="">Select component or variable</option>
            {sources.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}
          </select>
        </label>
        <button type="button" className={styles.smallButton} disabled={!source || !target} onClick={() => {
          if (source && target) onChange(insertRequestInput(action, target, source.template));
        }}>Insert reference</button>
      </div>
      {sources.length === 0 && <div className={styles.formHint}>Add a component or declare a variable before inserting a request value.</div>}
    </div>
  );
}
